import { useEffect, useMemo, useState, type MouseEvent, type ReactNode } from 'react';
import { Button } from '@/components/ui/button';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { Spinner } from '@/components/ui/spinner';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useConfirm } from '@/components/confirm/ConfirmProvider';
import { notifyError } from '@/lib/notify';
import { useEventCallback } from '../ui/hooks';
import { isObject } from '../ui/utils';
import { useAdjustmentState as useAdjustmentStateJs } from '../pages/QuotePage/context/QuoteAdjustment/AdjustmentContext';
import { useAdjustmentForm as useAdjustmentFormJs } from '../pages/QuotePage/context/QuoteAdjustment/AdjustmentFormContext';
import MacavityTrackingJs from './ProgressBar';
import MceMacavityTrackingJs from './MceMacavityTracking';

/* The quote adjustment contexts and the Macavity trackers are JavaScript (the trackers stay legacy, D18). */
type AdjustmentState = {
  adjusting?: boolean;
  setCanExecuteBind: (can: boolean) => void;
  setCanSaveAdjustment: (can: boolean) => void;
  setCanExportEndorsements: (can: boolean) => void;
};
// eslint-disable-next-line @typescript-eslint/no-explicit-any -- boundary: QuoteAdjustment/AdjustmentContext.jsx
const useAdjustmentState = useAdjustmentStateJs as any as () => AdjustmentState;
// eslint-disable-next-line @typescript-eslint/no-explicit-any -- boundary: QuoteAdjustment/AdjustmentFormContext.jsx
const useAdjustmentForm = useAdjustmentFormJs as any as () => { setValue: (path: string, value: unknown) => void };
// eslint-disable-next-line @typescript-eslint/no-explicit-any -- boundary: components/ProgressBar.jsx
const MacavityTracking = MacavityTrackingJs as any as (props: Record<string, unknown>) => JSX.Element;
// eslint-disable-next-line @typescript-eslint/no-explicit-any -- boundary: components/MceMacavityTracking.jsx
const MceMacavityTracking = MceMacavityTrackingJs as any as (props: Record<string, unknown>) => JSX.Element;

type Tab = { name: string; label: string };
// eslint-disable-next-line @typescript-eslint/no-explicit-any -- boundary: rows of the page's quote or policy data
type Row = { id?: unknown } & Record<string, any>;
// eslint-disable-next-line @typescript-eslint/no-explicit-any -- boundary: an AG Grid API handed up by a grid
type GridReference = { api?: any } | null | undefined;

export type ViewTabsProps = {
  tabs: (string | Tab)[];
  getLabel?: (name: string, label: string) => string | null | undefined;
  onTabChanged?: (tab: string) => void;
  gridReference?: GridReference;
  endorsementGridReference?: GridReference;
  selectedFields?: Row[];
  setQuoteData?: (update: (prev: Record<string, Row[]>) => Record<string, unknown>) => void;
  quoteData?: Record<string, Row[]> & { id?: unknown };
  setImportModal?: (open: boolean) => void;
  quotesView?: boolean;
  onExport?: (tab: string) => void;
  isAdjustmentSuccessFul?: unknown;
  adjustmentResponse?: unknown;
  setAdjustmentResponse?: unknown;
  updateMacavity?: unknown;
  setCurrentTab?: (tab: string) => void;
  currentTab?: string;
  isEditingMCE?: boolean;
  handleDeleteFee?: (e?: { preventDefault?: () => void }) => unknown;
  handleDeleteMCELocation?: () => unknown;
  exportInProgress?: boolean;
  handleDeleteInsureds?: () => unknown;
  handleDeleteLossPayee?: () => unknown;
  enableDelete?: boolean;
  handleDeleteHVV?: () => unknown;
  handleDeleteTemporaryLocations?: () => unknown;
  hasIVE?: boolean;
  mceId?: unknown;
  mceType?: unknown;
  onMceMacavityComplete?: unknown;
  /** Tab panels (TabsContent). The quote and application pages pass none and show their own. */
  children?: ReactNode;
  /** Classes for a band around the bar of tabs and their actions (the policy page's white band). */
  barClassName?: string;
  [rest: string]: unknown;
};

const DELETABLE_TABS = ['NAMED_INSURED', 'HIGH_VALUE_VEHICLES', 'TEMPORARY_LOCATIONS', 'LOSS_PAYEE'];

/** A page's tab bar, with the tab-specific actions at its end: Delete, Macavity progress, Export. */
export function ViewTabs(props: ViewTabsProps) {
  const {
    tabs: tabsProp,
    getLabel,
    onTabChanged,
    gridReference,
    selectedFields,
    setQuoteData,
    quoteData,
    setImportModal,
    quotesView = false,
    onExport,
    isAdjustmentSuccessFul,
    adjustmentResponse,
    setAdjustmentResponse,
    updateMacavity,
    setCurrentTab = () => {},
    currentTab,
    isEditingMCE,
    handleDeleteFee = () => {},
    handleDeleteMCELocation = () => {},
    exportInProgress,
    handleDeleteInsureds,
    handleDeleteLossPayee,
    enableDelete,
    handleDeleteHVV,
    handleDeleteTemporaryLocations,
    hasIVE = false,
    mceId,
    mceType,
    onMceMacavityComplete,
    children,
    barClassName,
  } = props;
  const { adjusting, setCanExecuteBind, setCanSaveAdjustment, setCanExportEndorsements } = useAdjustmentState();
  const { setValue } = useAdjustmentForm();
  const confirm = useConfirm();

  const tabs = useMemo<Tab[]>(() => {
    if (Array.isArray(tabsProp)) {
      return tabsProp.map((t) => (isObject(t) ? (t as Tab) : { label: `${t}`, name: `${t}` }));
    }
    return [];
  }, [tabsProp]);

  const [tab, setTab] = useState<string>(currentTab || tabs[0]?.name || '');

  useEffect(() => {
    if (tab !== 'BIND_FORMS' && quotesView) setCanExecuteBind(false);
    if (tab === 'MISC_VALUES' && quotesView) setCanSaveAdjustment(true);
    if (tab !== 'MISC_VALUES' && quotesView) setCanSaveAdjustment(false);
  }, [tab, quotesView, setCanExecuteBind, setCanSaveAdjustment]);

  const updateViewTab = useEventCallback((t: string) => onTabChanged && onTabChanged(t));

  useEffect(() => {
    updateViewTab(tab);
  }, [updateViewTab, tab]);

  useEffect(() => {
    if (currentTab) setTab(currentTab);
  }, [currentTab]);

  const handleTemplateDownload = () => {
    let headersCsv;
    if (tab === 'FEES') {
      headersCsv = ['FEE', 'Annual', 'Percentage of GWP'].join(',');
    } else {
      const columnDefs = gridReference?.api.getColumnDefs();
      if (!columnDefs || columnDefs.length === 0) {
        console.error('No column definitions found.');
        return;
      }
      const headerNames = columnDefs
        .filter((column: { headerName?: string }) => column.headerName)
        .map((column: { headerName: string }) => column.headerName);
      if (headerNames.length === 0) {
        console.error('No header names found.');
        return;
      }
      headersCsv = headerNames.join(',');
    }
    const blob = new Blob([headersCsv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.setAttribute('download', `${tab}_headers.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  if (!tabs.length) {
    return null;
  }

  const handleDeleteRow = (e?: MouseEvent | Event) => {
    const currentTable = ({ FEES: 'fees', COVERAGES: 'locations' } as Record<string, string>)[tab] as string;
    e?.preventDefault?.();
    const result = (quoteData?.[currentTable] ?? []).filter((item) => !(selectedFields ?? []).find((selectedItem) => selectedItem.id === item.id));
    if (adjusting) setValue(`quote.${currentTable}`, result);
    setQuoteData?.((pre) => ({ ...pre, [currentTable]: result }));
  };

  const handleDeleteConfirmation = async (e?: MouseEvent | Event) => {
    try {
      if (isEditingMCE) {
        if (tab === 'PREMIUM_CALCULATION') {
          await handleDeleteMCELocation();
        } else {
          await handleDeleteFee(e);
        }
      } else if (tab === 'NAMED_INSURED') {
        handleDeleteInsureds?.();
      } else if (tab === 'HIGH_VALUE_VEHICLES') {
        handleDeleteHVV?.();
      } else if (tab === 'TEMPORARY_LOCATIONS') {
        handleDeleteTemporaryLocations?.();
      } else if (tab === 'LOSS_PAYEE') {
        handleDeleteLossPayee?.();
      } else {
        handleDeleteRow(e);
      }
    } catch (error) {
      console.error('Error during delete operation:', error);
      notifyError('An error occurred while processing the delete operation. Please try again.');
    }
  };

  const askToDelete = async () => {
    const confirmed = await confirm({
      title: 'Are You Sure You Want to Delete?',
      description: 'This action cannot be undone.',
      confirmLabel: 'Delete Permanently',
      cancelLabel: 'Cancel',
      destructive: true,
    });
    if (confirmed) await handleDeleteConfirmation();
  };

  const showDelete = ((selectedFields?.length ?? 0) > 0 && adjusting) || (enableDelete && DELETABLE_TABS.includes(tab));
  const exportLabel = adjusting && ['FEES', 'COVERAGES', 'ENDORSEMENTS', 'MISC_VALUES'].includes(tab) ? 'Import / Export' : 'Export';
  const exportMenu = tab === 'FEES' || tab === 'COVERAGES';

  const exportButtonContent = exportInProgress ? (
    <span className="flex items-center gap-1.5 font-bold">
      Exporting <Spinner />
    </span>
  ) : (
    exportLabel
  );

  const bar = (
    <div className="flex w-full flex-row items-center gap-2 overflow-auto">
      {/* shadcn's default Tabs, as generated. Eleven tabs are wider than most windows, so the
            list scrolls sideways inside this wrapper rather than being restyled to fit. */}
      <div className="min-w-0 flex-1 overflow-x-auto overflow-y-hidden">
        <TabsList>
          {tabs.map((t) => {
            const label = getLabel ? getLabel(t.name, t.label) : t.label;
            if (!label) {
              return null;
            }
            // The Inventory Value tab keeps its IVE highlight (the --ive token, D5).
            const highlightIVE = t.name === 'INVENTORY_VALUE' && hasIVE;
            return (
              <TabsTrigger
                key={`ViewTab - ${t.name}`}
                value={t.name}
                className={highlightIVE ? 'bg-ive-tab data-active:bg-ive-tab-active data-active:font-semibold' : undefined}
              >
                {label}
              </TabsTrigger>
            );
          })}
        </TabsList>
      </div>
      <div className="flex shrink-0 flex-row items-center gap-2">
        {showDelete ? (
          <Button variant="ghost" className="text-primary-light" onClick={askToDelete}>
            Delete
          </Button>
        ) : null}
        {quotesView ? (
          <MacavityTracking
            quoteId={quoteData?.id}
            tab={tab}
            quoteData={quoteData}
            isAdjustmentSuccessFul={isAdjustmentSuccessFul}
            adjustmentResponse={adjustmentResponse}
            setAdjustmentResponse={setAdjustmentResponse}
            updateMacavity={updateMacavity}
          />
        ) : null}
        {mceId ? <MceMacavityTracking mceId={mceId} mceType={mceType} onComplete={onMceMacavityComplete} /> : null}
        {tab !== 'BIND_FORMS' && quotesView ? (
          exportMenu ? (
            <DropdownMenu>
              <DropdownMenuTrigger render={<Button variant="outline" className="mr-2.5 ml-1 h-[35px] font-bold" disabled={exportInProgress} />}>
                {exportButtonContent}
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="w-64 rounded-lg">
                <DropdownMenuItem onClick={handleTemplateDownload}>Download Template</DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() => {
                    onExport?.(tab);
                    setCanExportEndorsements(false);
                  }}
                >
                  Export
                </DropdownMenuItem>
                {adjusting ? <DropdownMenuItem onClick={() => setImportModal?.(true)}>Import</DropdownMenuItem> : null}
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <Button
              variant="outline"
              className="mr-2.5 ml-1 h-[35px] font-bold"
              disabled={exportInProgress}
              onClick={() => {
                onExport?.(tab);
                if (tab === 'ENDORSEMENTS') setCanExportEndorsements(true);
              }}
            >
              {exportButtonContent}
            </Button>
          )
        ) : null}
        {isEditingMCE ? (
          <Button variant="ghost" className="text-primary-light" onClick={(e) => handleDeleteConfirmation(e as unknown as MouseEvent)}>
            Delete
          </Button>
        ) : null}
      </div>
    </div>
  );

  return (
    <Tabs
      value={tab}
      onValueChange={(next) => {
        setTab(String(next));
        setCurrentTab(String(next));
      }}
      className="w-full gap-0"
    >
      {barClassName ? <div className={barClassName}>{bar}</div> : bar}
      {children}
    </Tabs>
  );
}
